import { useNavigate } from "react-router-dom";

import { BankLogo } from "@assets";
import { Button } from "@components/Button";
import { ButtonSize } from "@components/Button/types";
import { ErrorComponent } from "@components/ErrorComponent";
import { TextInput, TextInputType } from "@components/Input";
import { ListItem } from "@components/ListItem";
import { Loader } from "@components/Loader";
import { MainLayout, MainLayoutProps } from "@components/MainLayout";
import { SuccessModal } from "@components/Modals";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body } from "@components/Typography";
import { VALID_CURRENCY_INPUT_REGEX } from "@constants";
import { BeneficiaryType, Routes, UserStatus } from "@ctypes";
import {
  useContributionFlow,
  useCreateOneTimeDeposit,
  useErrorBanner,
  useModal,
  useUser,
} from "@hooks";
import { i18n, Translations } from "@i18n";
import { convertDollarToCents, formatCurrencyInput } from "@utils";
import { AccountStatusBanner } from "./AccountStatusBanner";
import { useStyles } from "./styles";

export const OneTimeContributionRoute = () => {
  const styles = useStyles();

  const navigate = useNavigate();
  const { showErrorBanner } = useErrorBanner();
  const { show } = useModal();

  const { user, beneficiaries, bankAccounts, loading, error } = useUser();

  const [createOneTimeDeposit, { loading: isCreatingOneTimeDeposit }] = useCreateOneTimeDeposit();

  const {
    contributionProps: { beneficiaryId, goalId, bankAccountId, amount = "" },
    setContributionProps,
  } = useContributionFlow();

  const props: MainLayoutProps = {
    pageHeader: i18n.t(Translations.OneTimeContributionRouteHeader),
  };

  const individual = beneficiaries?.find((b) => b?.id === beneficiaryId);
  const goal = individual?.goals?.find((g) => g?.id === goalId);
  const bankAccount = bankAccounts?.find((b) => b?.id === bankAccountId) ?? bankAccounts?.[0];

  const firstName =
    individual?.type === BeneficiaryType.Self
      ? i18n.t(Translations.OneTimeContributionRouteYouLabel)
      : (individual?.firstName ?? "");

  const goalName = goal?.name ?? "";

  const amountValue = Number(amount.replace(/[$,]/g, ""));

  const invalidAmountInput = !VALID_CURRENCY_INPUT_REGEX.test(amount);

  const isInvalidVerificationStatus = Boolean(
    user?.status &&
    [
      UserStatus.VerificationInProgress,
      UserStatus.Pending,
      UserStatus.New,
      UserStatus.VerificationFailed,
    ].includes(user.status),
  );

  const disabled =
    !beneficiaryId ||
    !goalId ||
    invalidAmountInput ||
    !amount.length ||
    !bankAccount?.id ||
    isInvalidVerificationStatus;

  const handleIndividual = () => {
    navigate(Routes.ContributionIndividual);
  };

  const handleGoal = () => {
    if (!individual) return;
    navigate(Routes.ContributionGoal);
  };

  const handleBankLink = () => {
    navigate(Routes.ContributionBankAccount);
  };

  const onChangeAmount = (input: string) => {
    const newAmount = formatCurrencyInput({ input, decimals: true });
    setContributionProps({ amount: newAmount });
  };

  const handleSubmit = async () => {
    if (!goalId) {
      return showErrorBanner();
    }

    try {
      const amountInCents = convertDollarToCents(amountValue);

      await createOneTimeDeposit({
        variables: {
          goalId,
          bankAccountId: bankAccount?.id,
          amountInCents,
        },
      });

      show(<SuccessModal onClick={() => navigate(-1)} />);
    } catch (e: any) {
      const message = e?.graphQLErrors?.[0]?.message;
      showErrorBanner(message);
    }
  };

  const amountError = (() => {
    if (invalidAmountInput) {
      return i18n.t(Translations.OneTimeContributionRouteAmountInvalidInput);
    }
    if (amount.length > 0 && amountValue < 1) {
      return i18n.t(Translations.OneTimeContributionRouteAmountInvalidMinimumAmount);
    }
    return undefined;
  })();

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  return (
    <MainLayout {...props}>
      <Spacing size={SpacingSizes.M} />
      <Body style={styles.text}>{i18n.t(Translations.OneTimeContributionRouteTitle)}</Body>
      <Spacing size={SpacingSizes.M} />

      <AccountStatusBanner />

      <div onClick={handleIndividual}>
        <TextInput
          label={i18n.t(Translations.OneTimeContributionRouteIndividualInputLabel)}
          placeholder={i18n.t(Translations.OneTimeContributionRouteIndividualInputPlaceholder)}
          inputType={TextInputType.Secondary}
          value={firstName}
          style={styles.input}
          readOnly
        />
      </div>
      <Spacing size={SpacingSizes.M} />

      <div onClick={handleGoal}>
        <TextInput
          label={i18n.t(Translations.OneTimeContributionRouteGoalInputLabel)}
          placeholder={i18n.t(Translations.OneTimeContributionRouteGoalInputPlaceholder)}
          inputType={TextInputType.Secondary}
          value={goalName}
          style={styles.input}
          disabled={!individual}
          readOnly
        />
      </div>
      <Spacing size={SpacingSizes.M} />

      <TextInput
        label={i18n.t(Translations.OneTimeContributionRouteAmountInputLabel)}
        placeholder={i18n.t(Translations.OneTimeContributionRouteAmountInputPlaceholder)}
        inputType={TextInputType.Secondary}
        style={styles.input}
        value={amount}
        error={amountError}
        onChangeText={onChangeAmount}
      />
      <Spacing size={SpacingSizes.M} />

      <ListItem
        leftIcon={<BankLogo />}
        title={bankAccount?.name ?? bankAccount?.accountNumber ?? ""}
        onClick={handleBankLink}
        style={styles.bank}
        rightIcon={null}
        disabled
      />
      <Spacing size={SpacingSizes.XL} />

      <Button
        title={i18n.t(Translations.OneTimeContributionRoutePrimaryCTA)}
        size={ButtonSize.Small}
        disabled={disabled}
        loading={isCreatingOneTimeDeposit}
        onClick={handleSubmit}
      />
      <Spacing size={SpacingSizes.M} />
    </MainLayout>
  );
};
